import { useId, useRef, type KeyboardEvent, type ReactNode } from 'react';
import { Icon, type IconName } from './icons/index.tsx';
import styles from './Tabs.module.css';

export interface TabItem<K extends string> {
  key: K;
  label: ReactNode;
  icon?: IconName;
}

export interface TabsProps<K extends string> {
  tabs: readonly TabItem<K>[];
  active: K;
  onChange: (key: K) => void;
  orientation?: 'horizontal' | 'vertical';
  label: string;
  /** Renders the panel for the active tab. */
  children: ReactNode;
}

/** WAI-ARIA tabs: arrow keys move between tabs, Home/End jump to the ends. */
export function Tabs<K extends string>({
  tabs,
  active,
  onChange,
  orientation = 'horizontal',
  label,
  children,
}: TabsProps<K>) {
  const id = useId();
  const refs = useRef<(HTMLButtonElement | null)[]>([]);
  const vertical = orientation === 'vertical';

  const focusTab = (index: number) => {
    const tab = tabs[index];
    if (!tab) return;
    onChange(tab.key);
    refs.current[index]?.focus();
  };

  const onKeyDown = (e: KeyboardEvent, index: number) => {
    const next = vertical ? 'ArrowDown' : 'ArrowRight';
    const prev = vertical ? 'ArrowUp' : 'ArrowLeft';
    if (e.key === next) {
      e.preventDefault();
      focusTab((index + 1) % tabs.length);
    } else if (e.key === prev) {
      e.preventDefault();
      focusTab((index - 1 + tabs.length) % tabs.length);
    } else if (e.key === 'Home') {
      e.preventDefault();
      focusTab(0);
    } else if (e.key === 'End') {
      e.preventDefault();
      focusTab(tabs.length - 1);
    }
  };

  return (
    <div className={[styles['tabs'], vertical ? styles['vertical'] : ''].join(' ')}>
      <div
        role="tablist"
        aria-label={label}
        aria-orientation={orientation}
        className={styles['list']}
      >
        {tabs.map((tab, i) => {
          const selected = tab.key === active;
          return (
            <button
              key={tab.key}
              ref={(el) => {
                refs.current[i] = el;
              }}
              id={`${id}-tab-${tab.key}`}
              type="button"
              role="tab"
              aria-selected={selected}
              aria-controls={`${id}-panel`}
              tabIndex={selected ? 0 : -1}
              className={styles['tab']}
              onClick={() => {
                onChange(tab.key);
              }}
              onKeyDown={(e) => {
                onKeyDown(e, i);
              }}
            >
              {tab.icon ? <Icon name={tab.icon} size="1.25rem" /> : null}
              <span>{tab.label}</span>
            </button>
          );
        })}
      </div>
      <div
        id={`${id}-panel`}
        role="tabpanel"
        aria-labelledby={`${id}-tab-${active}`}
        className={styles['panel']}
      >
        {children}
      </div>
    </div>
  );
}
